import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  IoClose, 
  IoFilter, 
  IoGrid, 
  IoTrendingUp, 
  IoTrendingDown,
  IoInformationCircle,
  IoExpand,
  IoContract,
  IoPencil
} from 'react-icons/io5';

const CategoryHeatmap = ({ data }) => {
  const [selectedXAxis, setSelectedXAxis] = useState('age'); // age, gender, interaction, nps
  const [showPositive, setShowPositive] = useState(true); // toggle between positive% and negative%
  const [selectedCategory, setSelectedCategory] = useState('all'); // for category filter
  const [expandedCategory, setExpandedCategory] = useState(null); // for subcategory drill-down
  const [selectedCell, setSelectedCell] = useState(null); // for evidence display
  const [evidenceData, setEvidenceData] = useState([]);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [grayThreshold, setGrayThreshold] = useState(30); // threshold for gray background
  const [showThresholdPopover, setShowThresholdPopover] = useState(false); // popover visibility

  // Handle mouse move for tooltip positioning
  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleCellHover = (cellData, isFirstRow) => {
    setHoveredCell({ ...cellData, isFirstRow });
  };

  const handleCellLeave = () => {
    setHoveredCell(null);
  };

  // Update evidence data when sentiment selection changes while panel is open
  useEffect(() => {
    if (selectedCell && selectedCell.data) {
      // Re-filter the evidence data based on the current sentiment selection
      const filteredData = (selectedCell.data || []).filter(item => {
        if (showPositive) {
          // Show positive reviews - check if this item has positive sentiment for the category/subcategory
          if (selectedXAxis === 'nps') {
            return item.sentiment === 'Promoter';
          } else {
            // For other dimensions, check category/subcategory sentiment
            const categories = item.processed_data?.categories || [];
            const subcategories = item.processed_data?.subcategories || [];
            
            // Check if this item has positive sentiment for the selected category/subcategory
            if (selectedCell.yType === 'category') {
              return categories.some(cat => cat.category === selectedCell.y && cat.sentiment === 'positive');
            } else if (selectedCell.yType === 'subcategory') {
              return subcategories.some(subcat => 
                subcat.category === selectedCell.yParent && 
                subcat.subcategory === selectedCell.y && 
                subcat.sentiment === 'positive'
              );
            }
          }
        } else {
          // Show negative reviews - check if this item has negative sentiment for the category/subcategory
          if (selectedXAxis === 'nps') {
            return item.sentiment === 'Detractor';
          } else {
            // For other dimensions, check category/subcategory sentiment
            const categories = item.processed_data?.categories || [];
            const subcategories = item.processed_data?.subcategories || [];
            
            // Check if this item has negative sentiment for the selected category/subcategory
            if (selectedCell.yType === 'category') {
              return categories.some(cat => cat.category === selectedCell.y && cat.sentiment === 'negative');
            } else if (selectedCell.yType === 'subcategory') {
              return subcategories.some(subcat => 
                subcat.category === selectedCell.yParent && 
                subcat.subcategory === selectedCell.y && 
                subcat.sentiment === 'negative'
              );
            }
          }
        }
        return false;
      });
      
      setEvidenceData(filteredData);
    }
  }, [showPositive, selectedCell, selectedXAxis]);

  // Extract the actual data array from the bankData structure
  const actualData = useMemo(() => data?.data || [], [data]);

  // Get all categories for filter
  const categories = useMemo(() => {
    if (!actualData || !Array.isArray(actualData)) return [];
    const cats = [...new Set(actualData.map(item => 
      item.processed_data?.categories?.[0]?.category || 'Unknown'
    ))].filter(cat => cat !== 'Unknown' && cat !== 'Others');
    return cats.sort();
  }, [actualData]);

  // Get X-axis dimension values
  const getXAxisValues = (dimension) => {
    if (!actualData || !Array.isArray(actualData)) return [];
    
    let values = [];
    switch (dimension) {
      case 'age':
        // Create age groups from the age field
        const ageGroups = new Set();
        actualData.forEach(item => {
          const age = parseInt(item.age) || 0;
          if (age > 0) {
            const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
            ageGroups.add(ageGroup);
          }
        });
        values = [...ageGroups];
        break;
      case 'gender':
        values = [...new Set(actualData.map(item => {
          const gender = item.gender;
          if (gender === 'M') return 'Male';
          if (gender === 'F') return 'Female';
          if (gender === 'T') return 'Other';
          return gender;
        }).filter(Boolean))];
        break;
      case 'interaction':
        values = [...new Set(actualData.map(item => 
          item.interactionSubCategory || item.interactionCategory
        ).filter(Boolean))];
        break;
      case 'nps':
        values = ['Promoter', 'Passive', 'Detractor'];
        break;
      default:
        values = [];
    }
    return values.sort();
  };

  // Get Y-axis values (categories and subcategories)
  const getYAxisValues = () => {
    if (!actualData || !Array.isArray(actualData)) return [];
    
    const result = [];
    
    categories.forEach(category => {
      if (selectedCategory === 'all' || selectedCategory === category) {
        result.push({ type: 'category', name: category, parent: null });
        
        // If this category is expanded, add its subcategories
        if (expandedCategory === category) {
          const subcategories = new Set();
          
          actualData.forEach(item => {
            // Look for subcategories in processed_data.subcategories array
            if (item.processed_data?.subcategories) {
              item.processed_data.subcategories.forEach(subcat => {
                // Only include subcategories that belong to this category (excluding "Others")
                if (subcat.category === category && subcat.subcategory !== 'Others') {
                  subcategories.add(subcat.subcategory);
                }
              });
            }
          });
          
          console.log(`Found ${subcategories.size} subcategories for expanded category "${category}":`, [...subcategories]);
          
          [...subcategories].sort().forEach(subcat => {
            result.push({ type: 'subcategory', name: subcat, parent: category });
          });
        }
      }
    });
    
    return result;
  };

  // Calculate heatmap data
  const calculateHeatmapData = () => {
    if (!actualData || !Array.isArray(actualData)) return [];
    
    const xValues = getXAxisValues(selectedXAxis);
    const yValues = getYAxisValues();
    const heatmapData = [];
    
    yValues.forEach(yItem => {
      xValues.forEach(xValue => {
        const cellData = calculateCellData(yItem, xValue);
        // Include all cells, even empty ones for debugging
        heatmapData.push({
          x: xValue,
          y: yItem.name,
          yType: yItem.type,
          yParent: yItem.parent,
          ...cellData
        });
      });
    });
    
    console.log('Heatmap data calculated:', {
      totalCells: heatmapData.length,
      nonEmptyCells: heatmapData.filter(cell => cell.total > 0).length,
      sampleCells: heatmapData.slice(0, 3)
    });
    
    return heatmapData;
  };

  // Calculate data for a specific cell
  const calculateCellData = (yItem, xValue) => {
    let relevantData = [];
    
    // Filter data based on X-axis dimension
    const filteredByX = actualData.filter(item => {
      switch (selectedXAxis) {
        case 'age':
          const age = parseInt(item.age) || 0;
          const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
          return ageGroup === xValue;
        case 'gender':
          const gender = item.gender;
          let genderLabel = gender;
          if (gender === 'M') genderLabel = 'Male';
          else if (gender === 'F') genderLabel = 'Female';
          else if (gender === 'T') genderLabel = 'Other';
          return genderLabel === xValue;
        case 'interaction':
          return item.interactionSubCategory === xValue || item.interactionCategory === xValue;
        case 'nps':
          const npsScore = parseInt(item.rawSentiment) || 0;
          if (xValue === 'Promoter') return npsScore >= 9;
          if (xValue === 'Passive') return npsScore >= 7 && npsScore <= 8;
          if (xValue === 'Detractor') return npsScore <= 6;
          return false;
        default:
          return false;
      }
    });

    // Filter by category/subcategory
    if (yItem.type === 'category') {
      relevantData = filteredByX.filter(item => {
        const categories = item.processed_data?.categories || [];
        return categories.some(cat => cat.category === yItem.name);
      });
    } else if (yItem.type === 'subcategory') {
      relevantData = filteredByX.filter(item => {
        // Look for subcategories in processed_data.subcategories array
        const subcategories = item.processed_data?.subcategories || [];
        return subcategories.some(subcat => 
          subcat.category === yItem.parent && subcat.subcategory === yItem.name
        );
      });
    }

    // Calculate sentiment stats - use appropriate sentiment source based on selectedXAxis
    const positive = relevantData.filter(item => {
      if (selectedXAxis === 'nps') {
        // When NPS Category is selected, use sentiment field for calculation
        return item.sentiment === 'Promoter';
      } else {
        // For other dimensions, use sentiment analysis from processed_data
        // Check category-level sentiment
        const categories = item.processed_data?.categories || [];
        const subcategories = item.processed_data?.subcategories || [];
        
        // For category rows, check if any category has positive sentiment
        if (yItem.type === 'category') {
          return categories.some(cat => cat.category === yItem.name && cat.sentiment === 'positive');
        } else if (yItem.type === 'subcategory') {
          // For subcategory rows, check if the specific subcategory has positive sentiment
          return subcategories.some(subcat => 
            subcat.category === yItem.parent && 
            subcat.subcategory === yItem.name && 
            subcat.sentiment === 'positive'
          );
        }
        
        // Fallback: check if overall analysis indicates positive sentiment
        return categories.some(cat => cat.sentiment === 'positive') || 
               subcategories.some(subcat => subcat.sentiment === 'positive');
      }
    }).length;
    
    const negative = relevantData.filter(item => {
      if (selectedXAxis === 'nps') {
        // When NPS Category is selected, use sentiment field for calculation
        return item.sentiment === 'Detractor';
      } else {
        // For other dimensions, use sentiment analysis from processed_data
        // Check category-level sentiment
        const categories = item.processed_data?.categories || [];
        const subcategories = item.processed_data?.subcategories || [];
        
        // For category rows, check if any category has negative sentiment
        if (yItem.type === 'category') {
          return categories.some(cat => cat.category === yItem.name && cat.sentiment === 'negative');
        } else if (yItem.type === 'subcategory') {
          // For subcategory rows, check if the specific subcategory has negative sentiment
          return subcategories.some(subcat => 
            subcat.category === yItem.parent && 
            subcat.subcategory === yItem.name && 
            subcat.sentiment === 'negative'
          );
        }
        
        // Fallback: check if overall analysis indicates negative sentiment
        return categories.some(cat => cat.sentiment === 'negative') || 
               subcategories.some(subcat => subcat.sentiment === 'negative');
      }
    }).length;
    
    const neutral = relevantData.length - positive - negative;
    const total = relevantData.length;
    
    const positivePercentage = total > 0 ? Math.round((positive / total) * 100) : 0;
    const negativePercentage = total > 0 ? Math.round((negative / total) * 100) : 0;
    
    // Debug logging for empty cells
    if (total === 0 && (yItem.name === categories[0] || yItem.name === getXAxisValues(selectedXAxis)[0])) {
      console.log('Debug empty cell:', {
        yItem: yItem.name,
        xValue,
        filteredByXCount: filteredByX.length,
        actualDataSample: actualData.slice(0, 2),
        selectedXAxis
      });
    }
    
    // Calculate sentiment distribution based on the 'sentiment' field for color weighting
    const sentimentCounts = {
      'Promoter': 0,
      'Detractor': 0,
      'Passive': 0
    };
    
    relevantData.forEach(item => {
      const sentiment = item.sentiment;
      if (sentimentCounts.hasOwnProperty(sentiment)) {
        sentimentCounts[sentiment]++;
      }
    });
    
    // Determine dominant sentiment for color weighting
    let dominantSentiment = 'neutral';
    if (total > 0) {
      const maxCount = Math.max(...Object.values(sentimentCounts));
      if (maxCount > 0) {
        dominantSentiment = Object.keys(sentimentCounts).find(key => sentimentCounts[key] === maxCount);
      }
    }

    return {
      total,
      positive,
      negative,
      neutral,
      positivePercentage,
      negativePercentage,
      data: relevantData,
      sentimentCounts,
      dominantSentiment
    };
  };

  // Get color intensity based on volume and sentiment field weighting
  const getCellColor = (cellData) => {
    // If absolute number is less than threshold, use gray background
    if (cellData.total < grayThreshold) {
      return '#f3f4f6'; // Gray-100
    }
    
    const maxVolume = Math.max(...heatmapData.map(cell => cell.total));
    const volumeIntensity = Math.min(cellData.total / maxVolume, 1);
    
    // Use sentiment field for weighting calculation
    let sentimentWeight = 0;
    const totalSentimentItems = Object.values(cellData.sentimentCounts || {}).reduce((sum, count) => sum + count, 0);
    
    if (totalSentimentItems > 0) {
      if (showPositive) {
        // For positive view, weight by Promoter percentage
        sentimentWeight = (cellData.sentimentCounts?.['Promoter'] || 0) / totalSentimentItems;
      } else {
        // For negative view, weight by Detractor percentage  
        sentimentWeight = (cellData.sentimentCounts?.['Detractor'] || 0) / totalSentimentItems;
      }
    }
    
    // Combine volume and sentiment weight
    // 60% weight on volume, 40% weight on sentiment field
    const combinedIntensity = (volumeIntensity * 0.6) + (sentimentWeight * 0.4);
    
    // Ensure minimum visibility and cap at 1
    const finalIntensity = Math.max(0.15, Math.min(combinedIntensity, 0.9));
    
    if (showPositive) {
      // Green scale for positive sentiment
      // Use different shades based on intensity
      if (finalIntensity >= 0.7) {
        return `rgba(22, 163, 74, ${finalIntensity})`; // Dark green
      } else if (finalIntensity >= 0.4) {
        return `rgba(34, 197, 94, ${finalIntensity})`; // Medium green
      } else {
        return `rgba(74, 222, 128, ${finalIntensity})`; // Light green
      }
    } else {
      // Red scale for negative sentiment
      // Use different shades based on intensity
      if (finalIntensity >= 0.7) {
        return `rgba(220, 38, 38, ${finalIntensity})`; // Dark red
      } else if (finalIntensity >= 0.4) {
        return `rgba(239, 68, 68, ${finalIntensity})`; // Medium red
      } else {
        return `rgba(248, 113, 113, ${finalIntensity})`; // Light red
      }
    }
  };

  // Handle cell click
  const handleCellClick = (cellData) => {
    setSelectedCell(cellData);
    
    // Filter evidence data based on selected sentiment
    const filteredData = (cellData.data || []).filter(item => {
      if (showPositive) {
        // Show positive reviews - check if this item has positive sentiment for the category/subcategory
        if (selectedXAxis === 'nps') {
          return item.sentiment === 'Promoter';
        } else {
          // For other dimensions, check category/subcategory sentiment
          const categories = item.processed_data?.categories || [];
          const subcategories = item.processed_data?.subcategories || [];
          
          // Check if this item has positive sentiment for the selected category/subcategory
          if (cellData.yType === 'category') {
            return categories.some(cat => cat.category === cellData.y && cat.sentiment === 'positive');
          } else if (cellData.yType === 'subcategory') {
            return subcategories.some(subcat => 
              subcat.category === cellData.yParent && 
              subcat.subcategory === cellData.y && 
              subcat.sentiment === 'positive'
            );
          }
        }
      } else {
        // Show negative reviews - check if this item has negative sentiment for the category/subcategory
        if (selectedXAxis === 'nps') {
          return item.sentiment === 'Detractor';
        } else {
          // For other dimensions, check category/subcategory sentiment
          const categories = item.processed_data?.categories || [];
          const subcategories = item.processed_data?.subcategories || [];
          
          // Check if this item has negative sentiment for the selected category/subcategory
          if (cellData.yType === 'category') {
            return categories.some(cat => cat.category === cellData.y && cat.sentiment === 'negative');
          } else if (cellData.yType === 'subcategory') {
            return subcategories.some(subcat => 
              subcat.category === cellData.yParent && 
              subcat.subcategory === cellData.y && 
              subcat.sentiment === 'negative'
            );
          }
        }
      }
      return false;
    });
    
    setEvidenceData(filteredData);
  };

  // Close evidence panel
  const closeEvidence = () => {
    setSelectedCell(null);
    setEvidenceData([]);
  };

  const heatmapData = calculateHeatmapData();
  const xValues = getXAxisValues(selectedXAxis);
  const yValues = getYAxisValues();

  // Get display labels
  const getXAxisLabel = () => {
    switch (selectedXAxis) {
      case 'age': return 'Age Group';
      case 'gender': return 'Gender';
      case 'interaction': return 'Interaction Type';
      case 'nps': return 'NPS Category';
      default: return 'Dimension';
    }
  };

  // Show loading state if data is not available
  if (!data || !actualData.length) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading heatmap data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('CategoryHeatmap Debug:', {
    dataLength: actualData.length,
    sampleItem: actualData[0],
    categories: categories.slice(0, 3),
    xAxisValues: getXAxisValues(selectedXAxis),
    selectedXAxis
  });

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <IoGrid className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-800">Category Heatmap Analysis</h2>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <IoFilter className="w-4 h-4 text-gray-600" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* X-Axis Selector */}
          <select
            value={selectedXAxis}
            onChange={(e) => setSelectedXAxis(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="age">Age Group</option>
            <option value="gender">Gender</option>
            <option value="interaction">Interaction Type</option>
            <option value="nps">NPS Category</option>
          </select>

          {/* Positive/Negative Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setShowPositive(true)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                showPositive 
                  ? 'bg-green-600 text-white' 
                  : 'text-gray-600 hover:text-green-600'
              }`}
            >
              <IoTrendingUp className="w-4 h-4 inline mr-1" />
              Positive %
            </button>
            <button
              onClick={() => setShowPositive(false)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                !showPositive 
                  ? 'bg-red-600 text-white' 
                  : 'text-gray-600 hover:text-red-600'
              }`}
            >
              <IoTrendingDown className="w-4 h-4 inline mr-1" />
              Negative %
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg relative">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Color intensity based on volume and sentiment impact</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gray-200"></div>
              <button
                onClick={() => setShowThresholdPopover(!showThresholdPopover)}
                className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 cursor-pointer"
              >
                <span>Low volume (&lt;{grayThreshold})</span>
                <IoPencil className="w-3 h-3" />
              </button>
              <div className={`w-4 h-4 rounded ${showPositive ? 'bg-green-200' : 'bg-red-200'}`}></div>
              <span>Low impact</span>
              <div className={`w-4 h-4 rounded ${showPositive ? 'bg-green-400' : 'bg-red-400'}`}></div>
              <span>Medium impact</span>
              <div className={`w-4 h-4 rounded ${showPositive ? 'bg-green-700' : 'bg-red-700'}`}></div>
              <span>High impact</span>
            </div>
          </div>
          <span>Click cells to view evidence →</span>
        </div>
        
        {/* Threshold Configuration Popover */}
        {showThresholdPopover && (
          <div className="absolute top-full left-4 z-20 mt-1 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
            <div className="p-4">
              <h4 className="text-sm font-medium text-gray-800 mb-2">Gray Background Threshold</h4>
              <p className="text-xs text-gray-600 mb-3">
                Cells with fewer than this number of responses will have gray background
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={grayThreshold}
                  onChange={(e) => setGrayThreshold(parseInt(e.target.value) || 30)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">responses</span>
              </div>
              <div className="flex justify-end mt-3">
                <button
                  onClick={() => setShowThresholdPopover(false)}
                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 focus:outline-none"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Color Calculation Note */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <IoInformationCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Color Intensity Calculation:</p>
            <p className="mb-2">
              <strong>Formula:</strong> (Volume Weight × 60%) + (Sentiment Impact × 40%)
            </p>
            <div className="space-y-1 text-xs">
              <p>• <strong>Gray Background:</strong> Applied to cells with fewer than {grayThreshold} responses (configurable threshold)</p>
              <p>• <strong>Volume Weight:</strong> Number of responses relative to highest cell count</p>
              <p>• <strong>Sentiment Impact:</strong> Percentage of positive/negative sentiment (0-100%)</p>
              <p>• <strong>Color Ranges:</strong> Light (0-40%), Medium (40-70%), Dark (70%+)</p>
              <p>• <strong>Examples:</strong> High volume + high sentiment % = Dark color | Low volume + high sentiment % = Medium color</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative h-full overflow-visible">
        {/* Heatmap Container - Full Width */}
        <div className="w-full h-full overflow-visible">
          <div className="overflow-x-auto border border-gray-200 rounded-lg h-full">
            <div className="min-w-max relative">
              {/* Header */}
              <div className="flex sticky top-0 bg-white z-30 shadow-sm">
                <div className="w-48 p-2 font-medium text-gray-700 border-b border-r border-gray-200 bg-gray-50">
                  Categories / {getXAxisLabel()}
                </div>
                {xValues.map(xValue => (
                  <div key={xValue} className="min-w-32 p-2 text-center font-medium text-gray-700 border-b border-gray-200 bg-gray-50">
                    <div className="whitespace-normal text-xs leading-tight" title={xValue}>
                      {xValue}
                    </div>
                  </div>
                ))}
              </div>

              {/* Rows */}
              {yValues.map((yItem, yIndex) => (
                <div key={`${yItem.type}-${yItem.name}`} className="flex relative">
                  {/* Y-axis label */}
                  <div className={`w-48 p-2 border-r border-b border-gray-200 flex items-center sticky left-0 z-20 relative group ${
                    yItem.type === 'category' 
                      ? 'bg-gray-50 font-medium' 
                      : 'bg-white pl-6 text-sm text-gray-600'
                  }`}>
                    {yItem.type === 'category' && (
                      <button
                        onClick={() => setExpandedCategory(
                          expandedCategory === yItem.name ? null : yItem.name
                        )}
                        className="mr-2 text-blue-600 hover:text-blue-800"
                      >
                        {expandedCategory === yItem.name ? <IoContract /> : <IoExpand />}
                      </button>
                    )}
                    <span className="truncate" title={yItem.name}>{yItem.name}</span>
                    
                    {/* Hover tooltip for truncated text */}
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 hidden group-hover:block z-50">
                      <div className="bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap max-w-xs shadow-lg">
                        {yItem.name}
                      </div>
                    </div>
                  </div>

                  {/* Data cells */}
                  {xValues.map((xValue, xIndex) => {
                    const cellData = heatmapData.find(
                      cell => cell.x === xValue && cell.y === yItem.name
                    );

                    // Determine if this is the first row for smart tooltip positioning
                    const isFirstRow = yIndex === 0;

                    return (
                      <motion.div
                        key={`${yItem.name}-${xValue}`}
                        className="min-w-32 h-12 border-b border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-400 flex flex-col items-center justify-center text-xs relative group"
                        style={{
                          backgroundColor: cellData ? getCellColor(cellData) : '#f9fafb'
                        }}
                        onClick={() => cellData && cellData.total > 0 && handleCellClick(cellData)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onMouseEnter={(e) => cellData && handleCellHover(cellData, isFirstRow)}
                        onMouseLeave={handleCellLeave}
                        onMouseMove={handleMouseMove}
                      >
                        {cellData ? (
                          <>
                            <div className="font-bold text-gray-800">
                              {showPositive ? cellData.positivePercentage : cellData.negativePercentage}%
                            </div>
                            <div className="text-gray-600">
                              ({showPositive ? cellData.positive : cellData.negative})
                            </div>
                          </>
                        ) : (
                          <div className="text-gray-400 text-xs">-</div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Evidence Panel - Absolute Positioned Overlay */}
        <AnimatePresence>
          {selectedCell && (
            <motion.div
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              className="absolute top-0 right-0 w-96 h-full bg-white border-l border-gray-300 shadow-xl z-50 overflow-hidden"
            >
              <div className="h-full flex flex-col">
                {/* Header - Fixed */}
                <div className="flex items-center justify-between p-4 pb-3 border-b border-gray-200 bg-gray-50">
                  <div>
                    <h3 className="font-medium text-gray-800">Customer Feedback</h3>
                    <p className="text-sm text-gray-600">
                      {selectedCell.y} × {selectedCell.x}
                    </p>
                    <p className="text-xs text-gray-500">
                      {evidenceData.length} {showPositive ? 'positive' : 'negative'} responses (out of {selectedCell.total} total)
                    </p>
                  </div>
                  <button
                    onClick={closeEvidence}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded"
                  >
                    <IoClose className="w-5 h-5" />
                  </button>
                </div>

                {/* Evidence List - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {evidenceData.length > 0 ? (
                    evidenceData.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className={`p-3 rounded-lg border-l-4 bg-white shadow-sm`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {/* Text Analysis Sentiment */}
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              item.sentiment.toLowerCase() === 'promoter' 
                                ? 'bg-green-100 text-green-800' 
                                : item.sentiment.toLowerCase() === 'detractor'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              Text: {(() => {
                                if (item.sentiment.toLowerCase() === 'promoter') return 'Positive';
                                if (item.sentiment.toLowerCase() === 'detractor') return 'Negative';
                                if (item.sentiment.toLowerCase() === 'passive') return 'Neutral';
                                return 'UNKNOWN';
                              })()}
                            </span>
                            
                            {/* NPS Sentiment */}
                            {item.rawSentiment !== undefined && (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                parseInt(item.rawSentiment) >= 9 
                                  ? 'bg-green-100 text-green-800'
                                  : parseInt(item.rawSentiment) >= 7
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                NPS: {(() => {
                                  const nps = parseInt(item.rawSentiment);
                                  if (nps >= 9) return `${nps} (Promoter)`;
                                  if (nps >= 7) return `${nps} (Passive)`;
                                  return `${nps} (Detractor)`;
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {item.text || 'No feedback available'}
                        </p>
                        <div className="flex items-center mt-2 text-xs text-gray-500 space-x-2">
                          {item.age && (
                            <span>Age: {(() => {
                              const age = parseInt(item.age) || 0;
                              return age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
                            })()}</span>
                          )}
                          {item.gender && (
                            <span>Gender: {item.gender === 'M' ? 'Male' : item.gender === 'F' ? 'Female' : item.gender === 'T' ? 'Other' : item.gender}</span>
                          )}
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 mt-8">
                      <IoInformationCircle className="w-8 h-8 mx-auto mb-2" />
                      <p>No {showPositive ? 'positive' : 'negative'} feedback available for this combination</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Portal-based Tooltip */}
        {hoveredCell && typeof document !== 'undefined' && createPortal(
          <div
            className="fixed pointer-events-none z-[9999] transition-opacity duration-200"
            style={{
              left: `${mousePosition.x + 10}px`,
              top: `${mousePosition.y - (hoveredCell.isFirstRow ? -10 : 50)}px`,
            }}
          >
            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-2xl border border-gray-700">
              <div>Total: {hoveredCell.total} responses</div>
              <div>Positive: {hoveredCell.positivePercentage}%</div>
              <div>Negative: {hoveredCell.negativePercentage}%</div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default CategoryHeatmap; 